import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PROTOCOL_CATEGORIES } from '../../utils/constants';
import './ProtocolTemplates.css';

export default function ProtocolTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, [user]);

  async function loadTemplates() {
    setLoading(true);
    const { data: prac } = await supabase
      .from('practitioners')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    if (prac) {
      const { data } = await supabase
        .from('protocol_templates')
        .select('*, template_items(*)')
        .eq('practitioner_id', prac.id)
        .order('created_at', { ascending: false });

      setTemplates(data || []);
    }
    setLoading(false);
  }

  async function deleteTemplate(templateId) {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;

    await supabase.from('template_items').delete().eq('template_id', templateId);
    await supabase.from('protocol_templates').delete().eq('id', templateId);
    loadTemplates();
  }

  function getCategoryInfo(key) {
    return PROTOCOL_CATEGORIES.find((c) => c.key === key) || { icon: '📋', label: key };
  }

  if (loading) {
    return (
      <div className="screen container">
        <div className="tab-loading">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="screen container">
      <h1 style={{ marginBottom: '6px' }}>Protocol Templates</h1>
      <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>
        Reusable protocol sets you can apply to any client. Save a template from a client's Protocol tab.
      </p>

      {templates.length === 0 ? (
        <div className="protocol-empty">
          <p>No templates yet.</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: '4px' }}>
            Open a client's protocol and click "Save as Template" to create one.
          </p>
        </div>
      ) : (
        <div className="tmpl-list">
          {templates.map((tmpl) => {
            const items = tmpl.template_items || [];
            const isOpen = expanded === tmpl.id;

            // Group items by category
            const grouped = PROTOCOL_CATEGORIES
              .map((cat) => ({
                ...cat,
                items: items.filter((i) => i.category === cat.key),
              }))
              .filter((g) => g.items.length > 0);

            return (
              <div key={tmpl.id} className="tmpl-card">
                <div className="tmpl-card-header" onClick={() => setExpanded(isOpen ? null : tmpl.id)}>
                  <div>
                    <h3 className="tmpl-name">{tmpl.name}</h3>
                    <p className="tmpl-meta">
                      {items.length} item{items.length !== 1 ? 's' : ''}
                      {tmpl.description && ` · ${tmpl.description}`}
                    </p>
                  </div>
                  <span className="tmpl-chevron">{isOpen ? '▾' : '▸'}</span>
                </div>

                {isOpen && (
                  <div className="tmpl-detail">
                    {grouped.map((group) => (
                      <div key={group.key} className="tmpl-group">
                        <span className="tmpl-group-label">{group.icon} {group.label}</span>
                        <ul className="tmpl-items">
                          {group.items.map((item) => (
                            <li key={item.id}>
                              <strong>{item.name}</strong>
                              {item.dosage && <span> — {item.dosage}</span>}
                              {item.instructions && <span className="tmpl-instructions"> ({item.instructions})</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    <button
                      className="btn btn-secondary tmpl-delete-btn"
                      onClick={() => deleteTemplate(tmpl.id)}
                    >
                      Delete Template
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
